import { useEffect, useState } from 'react';

import Tabs from '@/components/Tabs.jsx';
import { api } from '@/lib/api.js';
import { useSeasonData } from './useSeasonData.js';
import MatrixTab from './MatrixTab.jsx';
import FixtureTickerTab, { seasonHasFixtures } from './FixtureTickerTab.jsx';
import BarChartRaceTab from './BarChartRaceTab.jsx';
import {
	PointsTable,
	TeamTable,
	HaulsTable,
	HaulsByFixture,
	HaulsByPlayer,
} from './SeasonTabs.jsx';

/*
 * Season Archive — everything scoped to one season.
 *
 * The season selector lives here rather than on the page, which is what
 * makes its scope obvious: Player Archive and Top Seasons sit alongside
 * this panel and span every season, so a page-level selector would have
 * implied it applied to them too.
 *
 * Every tab below reads the same /api/history/season/<season> payload, so
 * useSeasonData fetches it once per season and caches it.
 */
export default function SeasonArchive({
	season: seasonFromPath,
	activeTab,
	onSelectTab,
	onSelectSeason,
	onResolveSeason,
}) {
	const [seasons, setSeasons] = useState([]);
	/* The path may not pin a season — bare /history does not — so fall back
	 * to the latest once the list arrives. */
	const [latestSeason, setLatestSeason] = useState(null);

	/* A season in the path is only honoured if it exists. An unknown one —
	 * a typo, or a link from before the archive changed — falls back to the
	 * latest rather than asking the API for a season it does not have. */
	const pathSeasonIsKnown =
		seasonFromPath && (!seasons.length || seasons.includes(seasonFromPath));
	const season = (pathSeasonIsKnown ? seasonFromPath : null) ?? latestSeason;
	/* Cached, so this costs nothing beyond what the tabs already fetch. It
	 * is only read to decide whether the fixture ticker has anything to
	 * show — past seasons carry no upcoming fixtures. */
	const { data: seasonData } = useSeasonData(season);

	useEffect(() => {
		api
			.get('/api/history/season')
			.then((list) => {
				if (!Array.isArray(list) || !list.length) return;
				setSeasons([...list].reverse());
				/* Only a fallback — writing it to the URL here would clobber a
				 * season pinned in a shared link. */
				setLatestSeason(list[list.length - 1]);
					/* Let the page build full paths before the URL pins one. */
					onResolveSeason?.(list[list.length - 1]);
			})
			.catch(() => setSeasons([]));
	}, []);

	/* Correct the URL rather than leaving it pointing at a season that does
	 * not exist. Replaces, so Back does not bounce off the bad link. */
	useEffect(() => {
		if (seasons.length && seasonFromPath && !seasons.includes(seasonFromPath) && latestSeason) {
			onSelectSeason?.(latestSeason, { replace: true });
		}
	}, [seasons, seasonFromPath, latestSeason, onSelectSeason]);

	if (!season) {
		return (
			<div className="tab-loading" role="status">
				<span className="spinner" aria-hidden="true" />
				<span>Loading seasons…</span>
			</div>
		);
	}

	/*
	 * Ordered season totals first, then the per-gameweek view, then hauls.
	 *
	 * Player Stats and Team Stats are the same view at two levels, so they
	 * sit together; the old order put GW Matrix between them. The three haul
	 * tabs run from the raw event list to its two aggregations.
	 *
	 * "Points Table" previously named the first tab, but that means the
	 * league standings elsewhere on the site (landing.html).
	 */
	const tabs = [
		{ id: 'player-stats', label: 'Player Stats', render: () => <PointsTable season={season} /> },
		{ id: 'team-stats', label: 'Team Stats', render: () => <TeamTable season={season} /> },
		{ id: 'gw-matrix', label: 'GW Matrix', render: () => <MatrixTab season={season} /> },
		...(seasonHasFixtures(seasonData)
			? [
					{
						id: 'fixture-ticker',
						label: 'Fixture Ticker',
						render: () => <FixtureTickerTab season={season} />,
					},
				]
			: []),
		{ id: 'double-digit-hauls', label: 'Double Digit Hauls', render: () => <HaulsTable season={season} /> },
		{
			id: 'hauls-by-player',
			label: 'Hauls by Player',
			render: () => <HaulsByPlayer season={season} />,
		},
		{
			id: 'hauls-by-fixture',
			label: 'Hauls by Fixture',
			render: () => <HaulsByFixture season={season} />,
		},
		{
			id: 'bar-chart-race',
			label: 'Bar Chart Race',
			render: () => <BarChartRaceTab season={season} />,
		},
	];

	return (
		<div className="season-archive">
			{/* A segmented control rather than loose pills: the nested tab
			    strip below is also pills, and two rounded rows stacked would
			    not say which is navigation and which is a filter. */}
			<div className="season-picker" role="group" aria-label="Season">
				<span className="season-picker__label">Season</span>
				<div className="season-picker__options">
					{seasons.map((s) => (
						<button
							key={s}
							type="button"
							className="season-picker__option"
							aria-pressed={s === season}
							onClick={() => onSelectSeason(s)}
						>
							{s}
						</button>
					))}
				</div>
			</div>

			{/* Remount on season change so each tab refetches cleanly. */}
			<Tabs
				key={season}
				tabs={tabs}
				variant="nested"
				activeId={activeTab ?? 'player-stats'}
				onChange={onSelectTab}
			/>
		</div>
	);
}
